import { ExternalLink, FileText, Receipt, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn, formatCurrency } from '@/lib/utils';
import { useBill } from '../hooks';
import type { BillDocument, BillPayment } from '../types';

const STATUS_COLOR: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  void: 'bg-gray-100 text-gray-800',
};

const DOC_ROLE_LABEL: Record<string, string> = {
  invoice: 'Vendor invoice',
  proof: 'Payment proof',
  receipt: 'Receipt',
  statement: 'Statement',
  other: 'Other',
};

function formatDateIso(date: string | null | undefined): string {
  if (!date) return '—';
  const m = String(date).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '—';
}

function dmsHref(dmsId: string): string {
  return `/dms/documents/${encodeURIComponent(dmsId)}`;
}

interface BillDetailPanelProps {
  billId: string | null;
  onClose: () => void;
}

export function BillDetailPanel({ billId, onClose }: BillDetailPanelProps) {
  const { data: bill, isLoading, error } = useBill(billId);

  if (!billId) return null;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-semibold">
              {bill?.doc_number ?? 'Bill detail'}
            </h2>
            {bill && (
              <Badge className={STATUS_COLOR[bill.status] ?? ''}>{bill.status}</Badge>
            )}
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {bill?.vendor_name ?? '—'}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {bill?.quickbooks_bill_id && (
            <a
              href={`https://qbo.intuit.com/app/bill?txnId=${bill.quickbooks_bill_id}`}
              target="_blank"
              rel="noreferrer"
              title="Open in QuickBooks"
              className="inline-flex h-8 items-center gap-1 rounded-md border border-input px-2 text-xs hover:bg-muted"
            >
              <ExternalLink className="h-3.5 w-3.5" /> QB
            </a>
          )}
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {isLoading && (
          <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {error && !isLoading && (
          <div className="py-10 text-center text-sm text-red-600">
            {(error as Error).message}
          </div>
        )}
        {bill && !isLoading && !error && (
          <div className="space-y-5">
            <section>
              <SectionLabel>Summary</SectionLabel>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Field label="Bill date" value={formatDateIso(bill.txn_date)} mono />
                <Field label="Due date" value={formatDateIso(bill.due_date)} mono />
                <Field label="Total" value={formatCurrency(Number(bill.total_amt))} mono align="right" />
                <Field
                  label="Balance"
                  value={formatCurrency(Number(bill.balance))}
                  mono
                  align="right"
                  emphasize={Number(bill.balance) !== 0}
                />
                <Field label="Currency" value={bill.currency || 'CAD'} />
                <Field label="QB Bill ID" value={bill.quickbooks_bill_id} mono />
              </dl>
            </section>

            {(bill.memo || bill.private_note) && (
              <section>
                <SectionLabel>Notes</SectionLabel>
                {bill.memo && (
                  <p className="text-sm whitespace-pre-wrap">{bill.memo}</p>
                )}
                {bill.private_note && (
                  <p className="mt-1 text-xs italic text-muted-foreground whitespace-pre-wrap">
                    {bill.private_note}
                  </p>
                )}
              </section>
            )}

            <section>
              <SectionLabel>
                <span className="inline-flex items-center gap-1">
                  <Receipt className="h-3.5 w-3.5" /> Payments ({bill.payments.length})
                </span>
              </SectionLabel>
              {bill.payments.length === 0 ? (
                <p className="text-xs text-muted-foreground">No recorded payments.</p>
              ) : (
                <ul className="space-y-2">
                  {bill.payments.map((p) => <PaymentRow key={p.id} payment={p} />)}
                </ul>
              )}
            </section>

            <section>
              <SectionLabel>
                <span className="inline-flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" /> Documents ({bill.documents.length})
                </span>
              </SectionLabel>
              {bill.documents.length === 0 ? (
                <p className="text-xs text-muted-foreground">No linked documents.</p>
              ) : (
                <ul className="space-y-2">
                  {bill.documents.map((d) => <DocumentRow key={d.id} doc={d} />)}
                </ul>
              )}
            </section>

            {bill.last_synced_at && (
              <p className="pt-2 text-[10px] text-muted-foreground">
                Last synced from QB {formatDateIso(bill.last_synced_at)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function Field({
  label, value, mono, align, emphasize,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  align?: 'left' | 'right';
  emphasize?: boolean;
}) {
  return (
    <div className={cn('flex flex-col', align === 'right' && 'items-end')}>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn(
        mono && 'font-mono tabular-nums',
        emphasize && 'font-semibold',
      )}>
        {value}
      </dd>
    </div>
  );
}

function PaymentRow({ payment }: { payment: BillPayment }) {
  const proofGdrive = payment.payment_proof_gdrive_url;
  const proofDms = payment.payment_proof_dms_id;
  return (
    <li className="rounded-md border border-border p-2 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-xs">{formatDateIso(payment.payment_date)}</span>
        <span className="font-mono tabular-nums">
          {formatCurrency(Number(payment.amount))}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
        {payment.payment_method && <span>{payment.payment_method}</span>}
        {payment.payment_account_name && <span>· {payment.payment_account_name}</span>}
        {payment.reference_number && <span>· Ref {payment.reference_number}</span>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {payment.quickbooks_bill_payment_id && (
          <a
            href={`https://qbo.intuit.com/app/billpayment?txnId=${payment.quickbooks_bill_payment_id}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> QB
          </a>
        )}
        {proofDms && (
          <a
            href={dmsHref(proofDms)}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <FileText className="h-3 w-3" /> DMS proof
          </a>
        )}
        {proofGdrive && (
          <a
            href={proofGdrive}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> Drive proof
          </a>
        )}
      </div>
      {payment.notes && (
        <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{payment.notes}</p>
      )}
    </li>
  );
}

function DocumentRow({ doc }: { doc: BillDocument }) {
  const label = DOC_ROLE_LABEL[doc.role] ?? doc.role;
  return (
    <li className="rounded-md border border-border p-2 text-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{label}</span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {formatDateIso(doc.linked_at)}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {doc.dms_document_id && (
          <a
            href={dmsHref(doc.dms_document_id)}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <FileText className="h-3 w-3" /> DMS
          </a>
        )}
        {doc.gdrive_url && (
          <a
            href={doc.gdrive_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> Drive
          </a>
        )}
      </div>
      {doc.notes && (
        <p className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{doc.notes}</p>
      )}
    </li>
  );
}
